<template>
  <BlogLayout>
    <Head :title="title" />

    <div class="_padding-y:2">
      <!-- Back to Courses Link -->
      <div class="_padding-top:2 _padding-bottom:1">
        <IContainer>
          <div class="_display:flex _align-items:center pt-4">
            <Link
              :href="route('page.courses')"
              class="_display:flex _text:primary _text:normal _padding-right:1">
              <ChevronLeftIcon
                class="_display:flex _align-self:center _margin-right:1/2 action-icon" />
              Back to Courses
            </Link>
          </div>
        </IContainer>
      </div>

      <!-- Course Header Section with Embedded Video -->
      <div class="_background:primary-dark _margin-bottom:2 _padding-y:3">
        <IContainer>
          <IRow middle>
            <!-- Left Column: Course Info -->
            <IColumn xs="12" :lg="hasPreviewVideo ? '7' : '12'">
              <div class="course-info-wrapper">
                <!-- Tags -->
                <div class="_display:flex _flex-wrap:wrap _justify-content:start _margin-bottom:1">
                  <div
                    class="_margin-right:1 _margin-bottom:1/2"
                    v-for="(tag, index) in course.tagged"
                    :key="index">
                    <div v-if="tag">
                      <div
                        class="button -sm -outline _text:semibold tag-on-button"
                        :title="tag.tag_name">
                        {{ tag.tag_name }}
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Course Title -->
                <h1 class="course-title _text:white _margin:0 _margin-bottom:1">
                  {{ course.name }}
                </h1>

                <!-- Share Course Component -->
                <div class="_display:flex _justify-content:start">
                  <div class="social-icons-container _display:flex _align-items:center">
                    <!-- LinkedIn Share -->
                    <a
                      href="#"
                      @click.prevent="shareCourse('linkedin')"
                      class="social-icon-link _margin-right:1"
                      aria-label="Share on LinkedIn"
                      title="Share on LinkedIn">
                      <LinkedInIcon class="social-icon" alt="Share on LinkedIn" />
                    </a>
                    <!-- Bluesky Share -->
                    <a
                      href="#"
                      @click.prevent="shareCourse('bluesky')"
                      class="social-icon-link _margin-right:1"
                      aria-label="Share on Bluesky"
                      title="Share on Bluesky">
                      <BlueskyIcon class="social-icon" alt="Share on Bluesky" />
                    </a>
                    <!-- Facebook Share -->
                    <a
                      href="#"
                      @click.prevent="shareCourse('facebook')"
                      class="social-icon-link"
                      aria-label="Share on Facebook"
                      title="Share on Facebook">
                      <FacebookIcon class="social-icon" alt="Share on Facebook" />
                    </a>
                  </div>
                </div>
              </div>
            </IColumn>

            <!-- Right Column: Preview Video (if configured) -->
            <IColumn v-if="hasPreviewVideo" xs="12" lg="5">
              <div class="preview-video-container">
                <div class="preview-video-wrapper">
                  <!-- Loading overlay -->
                  <div v-if="isLoadingVideo" class="video-loading-overlay">
                    <div class="loading-content">
                      <svg class="loading-spinner" viewBox="0 0 100 100" width="256" height="256">
                        <g transform="translate(50 50)">
                          <path d="m0 0 5.4502-9.4402h-10.901z" fill="#009eda">
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 0 0"
                              to="360 0 0"
                              dur="2s"
                              repeatCount="indefinite"
                              begin="0s" />
                          </path>
                          <path d="m7.5 -9.468 8.3398 14.018h-16.68z" fill="#f55">
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 0 0"
                              to="-360 0 0"
                              dur="3s"
                              repeatCount="indefinite"
                              begin="0s" />
                          </path>
                        </g>
                      </svg>
                    </div>
                  </div>

                  <!-- Video Player -->
                  <media-controller
                    v-if="videoSrc"
                    :class="{ 'is-loading': isLoadingVideo }"
                    class="hero-video-player">
                    <div class="preview-video-wrapper" @click="playVideo">
                      <!-- Thumbnail Image -->
                      <img
                        v-if="!isPlaying"
                        :src="`https://img.youtube.com/vi/${extractVideoId(previewVideoUrl)}/maxresdefault.jpg`"
                        alt="Video Thumbnail"
                        class="video-thumbnail" />

                      <!-- Play Button Overlay -->
                      <div v-if="!isPlaying" class="thumbnail-play-button">
                        <PlayIcon class="play-icon" />
                      </div>

                      <!-- YouTube Video (hidden until play) -->
                      <div v-show="isPlaying" class="video-player-container">
                        <youtube-video
                          ref="heroVideoRef"
                          :src="videoSrc"
                          slot="media"
                          crossorigin="anonymous"
                          preload="metadata"
                          loading="lazy"
                          controls="false"
                          allowfullscreen
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          @loadedmetadata="handleVideoLoaded"
                          @error="handleVideoError" />
                      </div>
                    </div>
                  </media-controller>
                </div>
              </div>
            </IColumn>
          </IRow>
        </IContainer>
      </div>

      <!-- Course Content Section -->
      <div class="padding-y:4">
        <IContainer>
          <IRow>
            <IColumn xs="12" md="8">
              <!-- Course Description -->
              <div class="bg-white rounded shadow-md p-6 mb-6 border border-gray-100">
                <!-- Course Image -->
                <img
                  :src="course.courseImageUrl || courseImageUrl || '/images/no_course_image.png'"
                  :alt="course.name"
                  class="w-full h-auto bg-white rounded border border-gray-100 object-contain group-hover:scale-105 transition-transform duration-300" />

                <h2 class="text-2xl font-bold text-gray-800 mt-4">About This Course</h2>
                <div
                  class="prose prose-lg max-w-none _text:dark-30"
                  v-html="course.description"></div>

                <!-- Trainer Avatar Section -->
                <div v-if="course.trainers && course.trainers.length > 0">
                  <div
                    v-for="(trainer, index) in course.trainers"
                    :key="trainer.id || index"
                    class="_margin-y:1 _padding:2 _border _border-color:light _background:white">
                    <!-- Name/Title and Avatar Row -->
                    <div class="_display:flex _align-items:center _margin-bottom:1/2">
                      <!-- Trainer Avatar -->
                      <img
                        v-if="trainer.user && trainer.user.avatar_url"
                        :src="trainer.user.avatar_url"
                        class="avatar _margin-right:1/2 _border:1px _border-color:light"
                        alt="Trainer Avatar" />
                      <img
                        v-else
                        src="/images/default_post.png"
                        class="avatar _margin-right:1/2 _border:1px _border-color:light"
                        alt="Default Avatar" />
                      <div class="_display:flex _flex-direction:column">
                        <div class="_display:flex">
                          <h4
                            v-if="trainer.user && trainer.user.name"
                            class="_text:dark-40 _text:semibold _text:lg _margin:0">
                            {{ trainer.user.name }}
                          </h4>
                          <a
                            v-if="trainer.user && trainer.user.linkedin_url"
                            :href="trainer.user.linkedin_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="_color:primary hover:_color:primary-dark _transition:colors _duration:200"
                            :title="trainer.user.name ? `View ${trainer.user.name}'s LinkedIn Profile` : 'View LinkedIn Profile'">
                            <svg class="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                              <path
                                fill-rule="evenodd"
                                d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                                clip-rule="evenodd"></path>
                            </svg>
                          </a>
                        </div>
                        <p
                          v-if="trainer.user && trainer.user.title"
                          class="_text:dark-25 _text:semibold _text:md _margin:0">
                          {{ trainer.user.title }}
                        </p>
                      </div>
                    </div>

                    <!-- Bio Row with View More functionality -->
                    <div v-if="trainer.user && trainer.user.bio" class="_margin-left:0">
                      <div class="bio-container">
                        <p
                          class="_text:dark-30 _text:md _text:normal _margin:0 bio-text"
                          :class="{ 'bio-collapsed': !expandedBios[trainer.id || index] }">
                          {{ trainer.user.bio }}
                        </p>
                        <a
                          v-if="trainer.user && shouldShowViewMore(trainer.user.bio)"
                          @click.prevent="toggleBio(trainer.id || index)"
                          href="#"
                          class="_text:primary _text:sm _margin-top:1/2 _display:inline-block _cursor:pointer">
                          {{ expandedBios[trainer.id || index] ? 'View Less' : 'View More' }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Syllabus Section -->
              <div
                class="bg-white rounded-md shadow-md overflow-hidden border border-gray-100 _margin-y:2">
                <el-collapse
                  v-model="activePanel"
                  accordion
                  style="
                    --el-collapse-header-bg-color: #009ddb;
                    --el-collapse-header-text-color: white;
                    --el-collapse-border-color: #009ddb;
                  "
                  class="syllabus-collapse">
                  <el-collapse-item name="show-lessons">
                    <template #title>
                      <Bars3BottomLeftIcon class="ml-2 list-icon" style="color: white" />
                      <span class="text-lg font-semibold ml-3" style="color: white">
                        COURSE SYLLABUS
                      </span>
                    </template>

                    <!-- Syllabus Content -->
                    <div class="syllabus-content">
                      <div
                        v-if="course.pods?.length > 0"
                        v-for="(item, index) in course.pods"
                        :key="index">
                        <div
                          class="pod-item m-4 p-6 bg-white rounded-md border border-gray-200 hover:shadow-md transition-shadow">
                          <div class="pod-title flex items-center mb-4">
                            <div
                              class="module-indicator bg-primary text-white px-3 py-2 rounded-md font-semibold mr-4">
                              {{ index + 1 }}
                            </div>
                            <div class="text-gray-800 font-semibold text-xl">
                              {{ item.title }}
                            </div>
                          </div>
                          <div
                            class="_text:dark-30 text-lg leading-relaxed mb-4"
                            v-html="item.description"></div>

                          <!-- Lesson Count and Toggle -->
                          <div class="flex items-center justify-between mb-4">
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-md border border-gray-200 text-md font-medium bg-[#f9fafb] text-gray-800">
                              <span class="font-bold pr-1">{{ item.lesson?.length || 0 }}</span>
                              {{ (item.lesson?.length || 0) === 1 ? 'Lesson' : 'Lessons' }}
                            </span>

                            <!-- Show/Hide Lessons Button -->
                            <button
                              v-if="item.lesson && item.lesson.length > 0"
                              @click="toggleLessons(index)"
                              class="text-primary hover:text-primary-dark font-semibold text-md transition-colors flex items-center">
                              <span>{{ expandedLessons[index] ? 'Hide' : 'Show' }} Lessons</span>
                              <ChevronDownIcon
                                class="ml-1 w-5 h-5 transition-transform"
                                :class="{ 'rotate-180': expandedLessons[index] }" />
                            </button>
                          </div>

                          <!-- Lessons List -->
                          <div
                            v-if="item.lesson && item.lesson.length > 0 && expandedLessons[index]"
                            class="lessons-list">
                            <div
                              v-for="(lesson, lessonIndex) in item.lesson"
                              :key="lesson.id || lessonIndex"
                              class="lesson-item mb-4 mt-4 pb-4 border-b border-gray-200 last:border-b-0 last:mb-0 last:pb-0">
                              <div class="flex items-start">
                                <div class="flex-1">
                                  <div class="flex items-center gap-2">
                                    <!-- Type Icon -->
                                    <VideoCameraIcon
                                      v-if="lesson.type?.value === 'video'"
                                      class="w-5 h-5 text-primary flex-shrink-0"
                                      title="Video" />
                                    <DocumentTextIcon
                                      v-else-if="lesson.type?.value === 'text'"
                                      class="w-5 h-5 text-primary flex-shrink-0"
                                      title="Text" />
                                    <h4 class="text-gray-500 font-normal text-lg my-0">
                                      {{ lesson.title }}
                                    </h4>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-gray-500 text-center p-8">No lesson pods found.</div>
                    </div>
                  </el-collapse-item>
                </el-collapse>
              </div>
            </IColumn>

            <!-- Simplified Action Sidebar -->
            <IColumn xs="12" md="4">
              <div class="purchase-box-container">
                <div class="purchase-box bg-white rounded-md shadow-md p-6 border border-gray-100">
                  <!-- Course Stats -->
                  <div class="grid grid-cols-2 gap-4 mb-6 text-center">
                    <div class="bg-gray-50 rounded-md p-4">
                      <UsersIcon class="w-8 h-8 text-primary mx-auto mb-2" />
                      <div class="text-2xl font-bold text-gray-800">
                        {{ course.enrollments?.length || 0 }}
                      </div>
                      <div class="text-sm _text:dark-30">Students</div>
                    </div>
                    <div class="bg-gray-50 rounded-md p-4">
                      <ClockIcon class="w-8 h-8 text-primary mx-auto mb-2" />
                      <div class="text-2xl font-bold text-gray-800">{{ lessonCount }}</div>
                      <div class="text-sm _text:dark-30">Lessons</div>
                    </div>
                  </div>

                  <!-- Price Display -->
                  <div class="text-center mb-6">
                    <!-- All-Access Subscription Course -->
                    <div v-if="course.is_subscription" class="mb-4">
                      <div class="text-4xl font-bold text-gray-400 line-through mb-3">
                        {{
                          castCurrency(
                            course.price,
                            course.currency_code || course.currency?.value || 'USD',
                          )
                        }}
                      </div>
                      <div
                        class="all-access-badge-large inline-block px-6 py-3 rounded-lg text-white text-md font-bold mb-2">
                        ✓ Included with All-Access
                      </div>
                      <div class="_text:dark-30 text-sm mt-2">
                        Access with your $79/month subscription
                      </div>
                    </div>

                    <!-- Temporarily FREE course (enrollment_code === 'FREE') -->
                    <div
                      v-else-if="
                        course.enrollment_code === 'FREE' && course.price && course.price > 0
                      "
                      class="text-4xl font-bold mb-2">
                      <div class="text-2xl line-through opacity-75 text-gray-500 mb-1">
                        {{
                          castCurrency(
                            course.price,
                            course.currency_code || course.currency?.value || 'USD',
                          )
                        }}
                      </div>
                      <span class="text-success">FREE</span>
                      <div class="text-sm font-normal _text:dark-30 mt-1">until Aug 31st</div>
                    </div>

                    <!-- Regular pricing (paid courses with or without promotions) -->
                    <div v-else>
                      <div class="flex flex-col items-center gap-2 mb-2">
                        <!-- Show original price as strikethrough if there is a promotion -->
                        <div
                          v-if="hasPromotion"
                          class="text-2xl line-through opacity-75 text-gray-500">
                          {{
                            castCurrency(
                              course.price,
                              course.currency_code || course.currency?.value || 'USD',
                            )
                          }}
                        </div>

                        <!-- Promo price -->
                        <span v-if="hasPromotion" class="text-4xl font-bold text-primary">
                          {{
                            castCurrency(
                              displayPrice,
                              course.currency_code || course.currency?.value || 'USD',
                            )
                          }}
                        </span>

                        <!-- Regular price -->
                        <span
                          v-else-if="!course.price || course.price === 0"
                          class="text-4xl font-bold text-success">
                          FREE
                        </span>
                        <span v-else class="text-4xl font-bold text-primary">
                          {{
                            castCurrency(
                              course.price,
                              course.currency_code || course.currency?.value || 'USD',
                            )
                          }}
                        </span>

                        <!-- Promo text on its own line, full-width, and centered -->
                        <div
                          v-if="hasPromotion"
                          class="gradient-background text-white text-sm font-semibold px-3 py-2 rounded-md w-full mt-2">
                          {{ coursePromo.promo_text }}
                        </div>

                        <!-- EU Badge for non-USD currencies - on separate line -->
                        <span
                          v-if="
                            (course.currency_code || course.currency?.value) &&
                            (course.currency_code || course.currency?.value) !== 'USD' &&
                            course.price > 0
                          "
                          class="bg-primary text-white px-3 py-1 rounded text-sm font-semibold">
                          🌍 EU Exclusive
                        </span>
                      </div>

                      <!-- "one-time payment" text - only show for regular paid courses -->
                      <div
                        v-if="
                          (course.price > 0 || hasPromotion) && course.enrollment_code !== 'FREE'
                        "
                        class="_text:dark-30 text-sm">
                        one-time payment
                      </div>
                    </div>
                  </div>

                  <!-- Simple Action Button -->
                  <div class="space-y-3">
                    <template v-if="isAuthenticated">
                      <Link
                        :href="route('dashboard.index')"
                        class="w-full bg-primary hover:bg-primary-dark hover:text-white hover:no-underline text-white py-4 px-6 rounded-md font-semibold text-lg text-center block transition-colors">
                        Go to Dashboard
                      </Link>
                    </template>
                    <template v-else>
                      <Link
                        :href="loginUrl"
                        class="w-full bg-primary hover:bg-primary-dark hover:no-underline hover:text-gray-50 text-white py-4 px-0 rounded-md font-semibold text-lg text-center block transition-colors">
                        Login to Access Course
                      </Link>
                    </template>
                  </div>

                  <!-- Course Info -->
                  <div class="_xs:hidden _lg:visible mt-6 pt-6 border-t border-gray-200">
                    <p class="_text:dark-30 text-center">
                      <template v-if="isAuthenticated">
                        Manage your learning from your dashboard.
                      </template>
                      <template v-else>Log in to access and track your course progress.</template>
                    </p>
                  </div>
                </div>
              </div>
            </IColumn>
          </IRow>
        </IContainer>
      </div>
    </div>
    <IContainer>
      <!-- CTA Section -->
      <div class="relative overflow-hidden rounded-md bg-[#09315f] border-2 border-[#009ddb] mb-0">
        <!-- SVG Grid Background -->
        <div class="absolute inset-0 w-full h-full opacity-50">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 600 300"
            preserveAspectRatio="none"
            class="w-full h-full">
            <!-- Diagonal lines -->
            <line
              x1="0"
              y1="0"
              x2="600"
              y2="300"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="100"
              y1="0"
              x2="600"
              y2="250"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="200"
              y1="0"
              x2="600"
              y2="200"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="300"
              y1="0"
              x2="600"
              y2="150"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="400"
              y1="0"
              x2="600"
              y2="100"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="500"
              y1="0"
              x2="600"
              y2="50"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />

            <!-- Horizontal lines -->
            <line
              x1="0"
              y1="50"
              x2="600"
              y2="50"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="0"
              y1="100"
              x2="600"
              y2="100"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="0"
              y1="150"
              x2="600"
              y2="150"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="0"
              y1="200"
              x2="600"
              y2="200"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
            <line
              x1="0"
              y1="250"
              x2="600"
              y2="250"
              stroke="rgba(255, 255, 255, 0.5)"
              stroke-width="1.5" />
          </svg>
        </div>

        <!-- Tint Overlay (Optional) -->
        <div class="absolute inset-0 w-full h-full bg-[#092e58] opacity-70"></div>

        <!-- Content (must be positioned relative to appear above image) -->
        <i-row center class="relative z-10">
          <div
            class="container flex flex-col flex-wrap content-center justify-center pt-8 md:pt-10 lg:pt-12">
            <h2
              class="text-gray-100 text-center text-3xl sm:text-4xl md:text-5xl mb-2 md:mb-3 px-2 md:px-4 font-semibold leading-tight">
              The Clean Economy is Moving Fast. Are You?
            </h2>
            <p
              class="text-gray-300 text-center font-normal text-lg md:text-xl lg:text-2xl px-8 sm:px-8 md:px-16 lg:px-20 mb-8 mt-2 max-w-5xl mx-auto">
              Build specialized climate skills and AI workflows that boost earning potential by 40%
              or more.
            </p>
          </div>

          <i-column xs="12" lg="8" xl="8" class="pb-8 md:pb-10 lg:pb-12">
            <div class="flex justify-center gap-4 px-4 flex-wrap">
              <template v-if="isAuthenticated">
                <Link
                  :href="route('dashboard.index')"
                  class="button -lg -primary _text:semibold px-8 py-4 rounded-lg font-semibold hover:bg-[#007bb8] transition-colors duration-200">
                  Go to Dashboard
                </Link>
              </template>
              <template v-else>
                <Link
                  :href="loginUrl"
                  class="button -lg -primary _text:semibold px-8 py-4 rounded-lg font-semibold hover:bg-[#007bb8] transition-colors duration-200">
                  Login to Access
                </Link>
                <Link
                  :href="route('register')"
                  class="button -lg -outline _text:semibold px-8 py-4 rounded-lg font-semibold border-2 border-white text-white hover:bg-white hover:text-[#09315f] transition-colors duration-200">
                  Create Account
                </Link>
              </template>
            </div>
          </i-column>
        </i-row>
      </div>
    </IContainer>
  </BlogLayout>
</template>

<script setup>
  import { computed, onMounted, onUnmounted, onBeforeUnmount, reactive, ref } from 'vue'
  import { Head, Link } from '@inertiajs/vue3'
  import { useMeta } from '@/Composables/useMeta'
  import BlogLayout from '@/Layouts/BlogLayout.vue'
  import { castCurrency } from '@/Services/castCurrency.js'
  import {
    Bars3BottomLeftIcon,
    ChevronLeftIcon,
    ChevronDownIcon,
    ClockIcon,
    UsersIcon,
    VideoCameraIcon,
    DocumentTextIcon,
    PlayIcon,
  } from '@heroicons/vue/24/solid'
  import LinkedInIcon from '@/Components/LinkedInIcon.vue'
  import BlueskyIcon from '@/Components/BlueskyIcon.vue'
  import FacebookIcon from '@/Components/FacebookIcon.vue'
  import config from '@config'

  // Define props
  const props = defineProps({
    title: {
      type: String,
      default: 'Course Details',
    },
    course: Object,
    isAuthenticated: Boolean,
    loginUrl: String,
    courseImageUrl: String,
    errors: Object,
    meta: Object,
  })

  // Strip HTML function
  const stripHtml = (html) => {
    if (!html) return ''

    if (typeof window !== 'undefined') {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    }

    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\s+/g, ' ')
      .trim()
  }

  const activePanel = ref('show-lessons')
  const expandedLessons = reactive({})
  const expandedBios = reactive({})

  // Hero video state
  const isLoadingVideo = ref(true)
  const heroVideoRef = ref(null)
  const videoKey = ref(0)

  // Extract YouTube video ID from various URL formats
  const extractVideoId = (url) => {
    if (!url) return null

    const youtubeShortMatch = url.match(/youtu\.be\/([^?]+)/)
    if (youtubeShortMatch) return youtubeShortMatch[1]

    const youtubeLongMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/)
    if (youtubeLongMatch) return youtubeLongMatch[1]

    const youtubeEmbedMatch = url.match(/youtube\.com\/embed\/([^?]+)/)
    if (youtubeEmbedMatch) return youtubeEmbedMatch[1]

    return null
  }

  // YouTube Preview functionality
  const previewVideoUrl = computed(() => {
    const courseConfig = config.course_previews?.[props.course.id]

    if (courseConfig?.enabled && courseConfig?.youtube_url) {
      return courseConfig.youtube_url
    }

    return null
  })

  const hasPreviewVideo = computed(() => {
    return !!previewVideoUrl.value
  })

  // Create YouTube video source URL
  const videoSrc = computed(() => {
    const videoId = extractVideoId(previewVideoUrl.value)
    if (!videoId) return null
    return `https://www.youtube.com/watch?v=${videoId}`
  })

  const handleVideoLoaded = () => {
    isLoadingVideo.value = false
  }

  const handleVideoError = (error) => {
    console.error('YouTube video error:', error)
    isLoadingVideo.value = false
  }

  // Computed properties
  const lessonCount = computed(() => {
    let count = 0
    if (props.course.pods) {
      props.course.pods.forEach((pod) => {
        if (pod.lesson) {
          count += pod.lesson.length
        }
      })
    }
    return count
  })

  const metaDescription = computed(() => {
    if (props.course?.meta_description || props.course?.summary) {
      return props.course.meta_description || props.course.summary
    }
    return stripHtml(props.course?.description) || 'Climate education course details.'
  })

  // Toggle lessons visibility for a specific pod
  const toggleLessons = (podIndex) => {
    expandedLessons[podIndex] = !expandedLessons[podIndex]
  }

  const toggleBio = (trainerId) => {
    expandedBios[trainerId] = !expandedBios[trainerId]
  }

  const shouldShowViewMore = (bio) => {
    if (!bio) return false
    return bio.length > 200
  }

  // Social sharing function
  const shareCourse = (platform) => {
    if (typeof window === 'undefined') return

    const url = window.location.href
    const title = props.course?.name || 'Course Details'

    let shareUrl = ''

    switch (platform) {
      case 'bluesky':
        shareUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(`Check out this course: ${title} ${url}`)}`
        break
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(`Check out this course: ${title}`)}`
        break
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(`Check out this course: ${title}`)}`
        break
      default:
        return
    }

    window.open(shareUrl, '_blank')
  }

  // Route function
  const route = (name, params = {}) => {
    const routes = {
      'page.courses': '/page/courses',
      'dashboard.index': '/dashboard',
      register: '/register',
    }
    return routes[name] || '/'
  }

  // Sticky behavior for purchase box
  onMounted(() => {
    const purchaseBox = document.querySelector('.purchase-box')
    const purchaseBoxContainer = document.querySelector('.purchase-box-container')
    const navbarHeight = 100
    const stickyBreakpoint = 992

    if (!purchaseBox || !purchaseBoxContainer) return

    let boxRect = purchaseBox.getBoundingClientRect()
    let containerRect = purchaseBoxContainer.getBoundingClientRect()
    let rightOffset = window.innerWidth - containerRect.right

    purchaseBoxContainer.style.minHeight = `${boxRect.height}px`

    const handleScroll = () => {
      const scrollY = window.scrollY
      const headerHeight = 400

      if (scrollY > headerHeight) {
        if (window.innerWidth < stickyBreakpoint) {
          if (purchaseBox.classList.contains('fixed-position')) {
            purchaseBox.classList.remove('fixed-position')
            purchaseBox.style.width = ''
            purchaseBox.style.right = ''
            purchaseBox.style.top = ''
            purchaseBox.style.position = ''
            purchaseBox.style.zIndex = ''
          }
          return
        }

        if (!purchaseBox.classList.contains('fixed-position')) {
          boxRect = purchaseBox.getBoundingClientRect()
          containerRect = purchaseBoxContainer.getBoundingClientRect()
          rightOffset = window.innerWidth - containerRect.right

          purchaseBox.classList.add('fixed-position')
          purchaseBox.style.position = 'fixed'
          purchaseBox.style.width = `${boxRect.width}px`
          purchaseBox.style.right = `${rightOffset}px`
          purchaseBox.style.top = `${navbarHeight + 60}px`
          purchaseBox.style.zIndex = '100'
        }
      } else {
        if (purchaseBox.classList.contains('fixed-position')) {
          purchaseBox.classList.remove('fixed-position')
          purchaseBox.style.position = ''
          purchaseBox.style.width = ''
          purchaseBox.style.right = ''
          purchaseBox.style.top = ''
          purchaseBox.style.zIndex = ''
        }
      }
    }

    const handleResize = () => {
      if (window.innerWidth < stickyBreakpoint) {
        if (purchaseBox.classList.contains('fixed-position')) {
          purchaseBox.classList.remove('fixed-position')
          purchaseBox.style.position = ''
          purchaseBox.style.width = ''
          purchaseBox.style.right = ''
          purchaseBox.style.top = ''
          purchaseBox.style.zIndex = ''
        }
        return
      }

      if (!purchaseBox.classList.contains('fixed-position')) {
        containerRect = purchaseBoxContainer.getBoundingClientRect()
        rightOffset = window.innerWidth - containerRect.right
      } else {
        containerRect = purchaseBoxContainer.getBoundingClientRect()
        rightOffset = window.innerWidth - containerRect.right
        purchaseBox.style.right = `${rightOffset}px`
      }
    }

    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', handleResize)

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleResize)
    })
  })

  // Clean up video on unmount
  onBeforeUnmount(() => {
    if (heroVideoRef.value) {
      try {
        heroVideoRef.value.pause()
      } catch (e) {
        // Silently fail
      }
    }
  })

  // Set meta data
  const { setMeta } = useMeta()

  if (props.meta) {
    setMeta(props.meta)
  } else {
    setMeta({
      title: props.title || props.course?.name,
      description: metaDescription.value,
      og_title: props.course?.name || 'Course Details',
      og_description: metaDescription.value,
      og_image: props.courseImageUrl || '/images/course-placeholder.jpg',
      canonical: props.course?.slug ? `/page/courses/${props.course.slug}` : undefined,
    })
  }

  // Check if this course has an active promotion
  const coursePromo = computed(() => {
    if (!config.course_promotions?.enabled) return null

    const promo = config.course_promotions.courses?.[props.course.id]

    if (!promo || promo.enabled === false) return null

    if (promo.expires_at) {
      const expiryDate = new Date(promo.expires_at)
      if (expiryDate < new Date()) return null
    }

    return promo
  })

  const displayPrice = computed(() => {
    return coursePromo.value?.promo_price_usd || props.course.price
  })

  const hasPromotion = computed(() => !!coursePromo.value)

  const isPlaying = ref(false)

  const playVideo = () => {
    isPlaying.value = true
    if (heroVideoRef.value) {
      heroVideoRef.value.play()
    }
  }
</script>

<style scoped>
  /* Hero Video Styles */
  .course-info-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-right: 2rem;
  }

  .course-title {
    font-size: 2.5rem;
    line-height: 1.2;
    font-weight: 700;
  }

  .preview-video-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  .preview-video-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow:
      0 10px 25px rgba(0, 0, 0, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1);
  }

  .hero-video-player {
    width: 100%;
    height: 100%;
    display: block;
    background: #000;
  }

  .hero-video-player youtube-video {
    width: 100%;
    height: 100%;
    display: block;
  }

  .hero-video-player.is-loading {
    opacity: 0.3;
  }

  .video-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #2c3e50, #3498db);
    z-index: 10;
    border-radius: 0.75rem;
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .loading-spinner {
    position: relative;
    left: -20px;
    width: 256px;
    height: 256px;
  }

  .loading-text {
    color: white;
    font-size: 0.875rem;
    font-weight: 600;
    margin: 0;
  }

  .preview-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    font-weight: 500;
    padding-left: 0.25rem;
  }

  .preview-icon-small {
    width: 18px;
    height: 18px;
    color: #009eda;
  }

  .custom-centered-play-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    pointer-events: none; /* Allows clicks to pass through to the button */
  }

  .custom-play-button {
    --media-play-button-size: 80px; /* Adjust size as needed */
    --media-icon-color: white;
    --media-background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
    pointer-events: auto; /* Ensures the button is clickable */
    transition: transform 0.2s ease;
  }

  .custom-play-button:hover {
    transform: scale(1.1);
  }

  .preview-video-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    background: #092e58;
    border-radius: 0.75rem;
    overflow: hidden;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .video-player-container {
    width: 100%;
    height: 100%;
  }

  .video-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Gradient Play Button Overlay */
  .thumbnail-play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #514a9d, #ff4242, #0db4f3);
    background-size: 200% 200%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.4),
      0 0 0 3px rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    animation: pulse 2s ease-in-out infinite;
  }

  .thumbnail-play-button:hover {
    transform: translate(-50%, -50%) scale(1.15);
    background-position: 100% 0;
    box-shadow:
      0 12px 32px rgba(0, 0, 0, 0.5),
      0 0 0 4px rgba(255, 255, 255, 0.3);
    animation: none;
  }

  .play-icon {
    width: 25px;
    height: 25px;
    color: white;
  }

  @keyframes pulse {
    0%,
    100% {
      box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.4),
        0 0 0 3px rgba(255, 255, 255, 0.2);
    }
    50% {
      box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.4),
        0 0 0 6px rgba(255, 255, 255, 0.3);
    }
  }

  @media (max-width: 767px) {
    .thumbnail-play-button {
      width: 72px;
      height: 72px;
    }

    .play-icon {
      width: 32px;
      height: 32px;
    }
  }

  /* Tag responsive handling */
  .tag-on-button {
    font-size: 0.875rem;
    padding: 0.375rem 0.75rem;
  }

  /* Social Icons */
  .social-icons-container {
    margin-top: 0.5rem;
  }

  .social-icon-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
  }

  .social-icon-link:hover {
    transform: translateY(-3px);
  }

  .social-icon {
    width: 24px;
    height: 24px;
    fill: white;
  }

  /* Icons */
  .action-icon {
    width: 20px;
    height: 20px;
    stroke-width: 0.2em;
  }

  .list-icon {
    position: relative;
    top: 0;
    width: 22px;
    height: 22px;
    stroke-width: 0.1em;
  }

  /* Syllabus Collapse Styling */
  .syllabus-collapse {
    border: none !important;
  }

  .syllabus-collapse :deep(.el-collapse-item__header) {
    background-color: #009ddb !important;
    color: white !important;
    border: none !important;
    padding: 16px 0 !important;
  }

  .syllabus-collapse :deep(.el-collapse-item__arrow) {
    color: white !important;
  }

  .syllabus-collapse :deep(.el-collapse-item__wrap) {
    border: none !important;
  }

  .syllabus-collapse :deep(.el-collapse-item__content) {
    padding: 0;
  }

  .pod-item {
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .pod-item:hover {
    transform: translateY(-2px);
  }

  .module-indicator {
    min-width: 40px;
    text-align: center;
  }

  /* Lessons List Styling */
  .lessons-list {
    margin-top: 1rem;
    padding-top: 0rem;
    border-top: 1px solid #e5e7eb;
  }

  .lesson-item {
    transition: background-color 0.2s ease;
  }

  .rotate-180 {
    transform: rotate(180deg);
  }

  /* Purchase Box Styling */
  .purchase-box-container {
    position: relative;
    min-height: 200px;
  }

  .purchase-box {
    transition: all 0.3s ease;
    transform: translateZ(0);
    will-change: position, top;
  }

  .purchase-box.fixed-position {
    position: fixed !important;
    z-index: 100 !important;
  }

  /* Color utilities */
  .bg-primary {
    background-color: #009ddb;
  }
  .bg-primary-dark {
    background-color: #092e58;
  }
  .text-primary {
    color: #009ddb;
  }
  .bg-success {
    background-color: #10b981;
  }
  .text-success {
    color: #10b981;
  }
  .hover\:bg-primary-dark:hover {
    background-color: #007bb5;
  }
  .hover\:text-primary-dark:hover {
    color: #007bb5;
  }

  .avatar {
    width: 76px;
    height: 76px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgb(228, 231, 237);
  }

  .bio-container {
    position: relative;
  }

  .bio-text {
    line-height: 1.5;
    transition: max-height 0.3s ease;
  }

  .bio-collapsed {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: 4.5em;
  }

  .bio-text:not(.bio-collapsed) {
    max-height: none;
  }

  .gradient-background {
    background: linear-gradient(to right, #514a9d, #ff4242, #0db4f3);
    background-size: 300% 100%;
    border: none;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 0.375rem;
    font-weight: 600;
  }

  /* Responsive adjustments */
  @media (max-width: 991px) {
    .course-info-wrapper {
      padding-right: 0;
      padding-bottom: 2rem;
    }

    .preview-video-container {
      order: -1;
      margin-bottom: 1.5rem;
    }

    .purchase-box {
      position: static !important;
      top: auto !important;
      margin-top: 0rem;
    }
  }

  @media (max-width: 767px) {
    .preview-video-wrapper {
      border-radius: 0.5rem;
    }

    .course-title {
      font-size: 1.75rem;
    }

    .tag-on-button {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }

    .social-icons-container {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }

  /* All-Access gradient badge (large version for detail page) */
  .all-access-badge-large {
    background: linear-gradient(to right, #514a9d, #0db4f3);
    border: none;
    box-shadow: 0 4px 12px rgba(81, 74, 157, 0.3);
  }
</style>
